import React from "react";
import Divider from "@material-ui/core/Divider";
import Typography from "@material-ui/core/Typography";
import Hidden from "@material-ui/core/Hidden";
import Tooltip from "@material-ui/core/Tooltip";
import { useTracking } from "react-tracking";
import { Error as ErrorIcon } from "@material-ui/icons";

import { ReactComponent as NoteIcon } from "assets/note.svg";
import { ReactComponent as RobotIcon } from "assets/robot.svg";
import { StyledGrid, Link, ScoreRange } from "components/simple";
import { UserCycleStatus } from "models/user";
import { color, fontSize, spacingDefaults } from "style/constants";
import { Score, ScoreRangeType } from "types";
import { getScoreName } from "./utils";

function buildScorePair(scores: Score[]): JSX.Element[] {
  return scores.reduce((acc: JSX.Element[], score: Score) => {
    if (score.scoreType === "percentile") acc[0] = <span key="0">{`${score.value}% /`}&nbsp;</span>;
    else acc[1] = <strong key="1">{score.value}</strong>;
    return acc;
  }, [] as JSX.Element[]);
}

function hasAutoScoringMethod(scores: Score[]): boolean {
  return scores.some(score => score.scoringMethod && !score.scoringMethod.startsWith("manual"));
}

interface SingleAssessmentProps {
  id: string;
  automatedScored: boolean;
  name: string;
  notes?: number;
  scores: Score[];
  scoreRanges: ScoreRangeType[];
  status: string;
  isClaimedByCurrentUser: boolean;
  nextAssesmentIds: any;
  validityCode?: number;
}

const moodScoreSortOrder = ["anxiety", "depression", "stress"];
const reasoningScoreSortOrder = ["T-Summ", "T-Interp", "T-Mem"];

const SingleAssessment: React.FC<SingleAssessmentProps> = ({
  id,
  name,
  notes,
  scores,
  scoreRanges,
  status,
  automatedScored,
  isClaimedByCurrentUser,
  nextAssesmentIds,
  validityCode = 0
}: SingleAssessmentProps) => {
  const tracking = useTracking();
  const canEdit =
    isClaimedByCurrentUser &&
    [
      UserCycleStatus.readyToScore,
      UserCycleStatus.scoringInProgress,
      UserCycleStatus.readyForReview
    ].includes(status as UserCycleStatus);

  const hasMultipleScores = scores.length > 3;
  const hasSingleScore = !hasMultipleScores;
  const isAboutYou = name === "About You";
  const isDigitSymbolSubstitution = name === "Digit Symbol";
  const scoresPairSpan = hasSingleScore || isDigitSymbolSubstitution ? buildScorePair(scores) : "";

  const raw = scores.find(s => s.scoreType === "raw");

  const sortedScores = scores.slice(0).sort((a, b) => {
    if (name === "Mood") {
      return moodScoreSortOrder.indexOf(a.scoreName) - moodScoreSortOrder.indexOf(b.scoreName);
    }

    if (name === "Reasoning") {
      return (
        reasoningScoreSortOrder.indexOf(a.scoreName) - reasoningScoreSortOrder.indexOf(b.scoreName)
      );
    }

    return 0;
  });

  let scoreCount = 0;

  return (
    <StyledGrid paddingLeft={spacingDefaults.large} paddingRight={spacingDefaults.large}>
      <Divider />
      <StyledGrid paddingTop={spacingDefaults.large} paddingBottom={spacingDefaults.large}>
        <StyledGrid container direction="row" justifyContent="space-between" alignItems="center">
          <StyledGrid alignItems="center" container item xs>
            <StyledGrid>
              <Link
                bold
                to={{
                  pathname: `/assessment/${id}`,
                  state: { inEditMode: false, nextAssesmentIds: nextAssesmentIds }
                }}
                onClick={() => {
                  tracking.trackEvent({
                    url: window.location.pathname,
                    actions: ["Assessments", "View Assessment", name]
                  });
                }}
              >
                {name}
              </Link>
              {hasAutoScoringMethod(scores) && (
                <Tooltip title="Auto-Scored">
                  <RobotIcon
                    width="14px"
                    height="14px"
                    style={{ marginLeft: "4px", verticalAlign: "middle" }}
                  />
                </Tooltip>
              )}
              {validityCode > 0 && (
                <Tooltip title="Invalid assessment — needs review">
                  <ErrorIcon
                    style={{
                      marginLeft: "4px",
                      verticalAlign: "middle",
                      color: color.RED,
                      fontSize: "18px"
                    }}
                  />
                </Tooltip>
              )}
            </StyledGrid>
            {notes && (
              <StyledGrid
                alignItems="center"
                aria-label={`${name}-note-count`}
                container
                fontSize={fontSize.small}
                item
                itemMargin="0.1rem"
                marginLeft={spacingDefaults.large}
                xs
              >
                <NoteIcon height="18px" width="18px" stroke={color.BLACK} />
                {notes}
              </StyledGrid>
            )}
          </StyledGrid>
          {!isAboutYou && (
            <StyledGrid container item xs={6} direction="row" justifyContent="space-between">
              <StyledGrid item>
                <Hidden mdDown>
                  {isDigitSymbolSubstitution
                    ? raw && <ScoreRange ranges={scoreRanges[0]} score={raw.value} />
                    : hasSingleScore &&
                      scoresPairSpan.length !== 0 &&
                      raw && <ScoreRange ranges={scoreRanges[0]} score={raw.value} />}
                </Hidden>
              </StyledGrid>
              <StyledGrid item>
                <StyledGrid
                  container
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  paddingRight={canEdit ? undefined : spacingDefaults.xlarge}
                >
                  {scores.length === 0 ? (
                    <Typography variant="body1" color="textSecondary">
                      <i>
                        {automatedScored || status === "assessments_in_progress"
                          ? "Assessment Not Started"
                          : "Needs Scoring"}
                      </i>
                    </Typography>
                  ) : (
                    scoresPairSpan
                  )}
                  {canEdit && (
                    <StyledGrid paddingLeft={spacingDefaults.normal}>
                      <Link
                        textDecoration={true}
                        fontSize={fontSize.small}
                        onClick={() => {
                          tracking.trackEvent({
                            url: window.location.pathname,
                            actions: ["Assessments", "Edit Assessment", name]
                          });
                        }}
                        to={{
                          pathname: `/assessment/${id}`,
                          state: { inEditMode: true, nextAssesmentIds: nextAssesmentIds }
                        }}
                        state
                      >
                        Edit
                      </Link>
                    </StyledGrid>
                  )}
                </StyledGrid>
              </StyledGrid>
            </StyledGrid>
          )}
        </StyledGrid>
        {!isDigitSymbolSubstitution && hasMultipleScores && (
          <>
            <StyledGrid paddingTop={spacingDefaults.large} paddingBottom={spacingDefaults.large}>
              <Divider />
            </StyledGrid>
            {sortedScores.map((score, index) => {
              const scoreName = getScoreName(score.scoreName);

              const singleScoreRange = scoreRanges.find(
                range => range.scoreName === score.scoreName
              );

              if (scoreName && score.scoreType === "raw") {
                scoreCount++;

                const percentile = scores.find(
                  s => s.scoreType === "percentile" && s.scoreName === score.scoreName
                );

                const raw = scores.find(
                  s => s.scoreType === "raw" && s.scoreName === score.scoreName
                );

                const scoresPairSpan = percentile
                  ? buildScorePair([score, percentile])
                  : buildScorePair([score]);

                return (
                  <StyledGrid paddingLeft={spacingDefaults.large} key={index}>
                    <StyledGrid
                      key={index}
                      container
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <StyledGrid>
                        <Typography variant="body1" color="textSecondary">
                          {scoreName}
                        </Typography>
                      </StyledGrid>
                      <StyledGrid item xs={6}>
                        <StyledGrid
                          container
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <StyledGrid paddingRight={spacingDefaults.xlarge}>
                            <Hidden mdDown>
                              {singleScoreRange && (
                                <ScoreRange ranges={singleScoreRange} score={raw ? raw.value : 0} />
                              )}
                            </Hidden>
                          </StyledGrid>
                          <StyledGrid paddingRight={spacingDefaults.xlarge}>
                            {scoresPairSpan}
                          </StyledGrid>
                        </StyledGrid>
                      </StyledGrid>
                    </StyledGrid>
                    {scoreCount <= 2 && (
                      <StyledGrid
                        paddingBottom={spacingDefaults.large}
                        paddingTop={spacingDefaults.large}
                      >
                        <Divider />
                      </StyledGrid>
                    )}
                  </StyledGrid>
                );
              } else {
                return null;
              }
            })}
          </>
        )}
      </StyledGrid>
    </StyledGrid>
  );
};

export default SingleAssessment;
